import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './Kashmir.css';

const Kashmir = () => {
  return (
    <>
      <Helmet>
        <title>Kashmir Tour - NomadTrailz</title>
        <meta name="description" content="Experience paradise on earth - Kashmir with NomadTrailz. Beautiful valleys, lakes, and snow-capped mountains." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755000434/xkaypfvzyqk2ih4djgvz.jpg?v=2" 
              alt="Kashmir" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Kashmir
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Paradise on earth with beautiful valleys, lakes, and snow-capped mountains
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Kashmir, often called "Paradise on Earth," is a region of breathtaking beauty 
                    with snow-capped mountains, pristine lakes, and lush valleys. Experience the 
                    rich culture, warm hospitality, and stunning landscapes that make Kashmir 
                    one of the most beautiful places in the world.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Dal Lake and Shikara rides</li>
                    <li>Gulmarg skiing and gondola</li>
                    <li>Pahalgam valley exploration</li>
                    <li>Sonamarg and Thajiwas glacier</li>
                    <li>Local Kashmiri cuisine</li>
                    <li>Traditional handicrafts and shopping</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Delhi to Srinagar</h4>
                    <ul>
                      <li>Depart from Delhi in the evening</li>
                      <li>Reach Srinagar early morning</li>
                      <li>Transfer to houseboat on Dal Lake</li>
                      <li>Enjoy Shikara ride on Dal Lake</li>
                      <li>Visit Mughal Gardens (Nishat & Shalimar)</li>
                      <li>Overnight stay at houseboat</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Srinagar to Gulmarg</h4>
                    <ul>
                      <li>Early morning departure to Gulmarg</li>
                      <li>Check-in at hotel in Gulmarg</li>
                      <li>Gondola ride to Phase 1 & 2 (own expense)</li>
                      <li>Explore Gulmarg market</li>
                      <li>Enjoy the scenic beauty of Gulmarg</li>
                      <li>Overnight stay at Gulmarg</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Gulmarg to Pahalgam</h4>
                    <ul>
                      <li>Morning departure to Pahalgam</li>
                      <li>Enroute visit Saffron fields</li>
                      <li>Check-in at hotel in Pahalgam</li>
                      <li>Visit Betaab Valley and Aru Valley</li>
                      <li>Horse riding at Pahalgam (own expense)</li>
                      <li>Overnight stay at Pahalgam</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Pahalgam to Sonamarg</h4>
                    <ul>
                      <li>Morning departure to Sonamarg</li>
                      <li>Enroute visit Kheer Bhawani Temple</li>
                      <li>Check-in at hotel in Sonamarg</li>
                      <li>Visit Thajiwas Glacier (own expense)</li>
                      <li>Enjoy the scenic beauty of Sonamarg</li>
                      <li>Overnight stay at Sonamarg</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Sonamarg to Srinagar</h4>
                    <ul>
                      <li>Morning departure to Srinagar</li>
                      <li>Visit Pari Mahal and Shankaracharya Temple</li>
                      <li>Local market shopping</li>
                      <li>Evening Shikara ride on Dal Lake</li>
                      <li>Overnight stay at houseboat</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Srinagar to Delhi</h4>
                    <ul>
                      <li>Morning free for shopping</li>
                      <li>Transfer to Srinagar Airport</li>
                      <li>Departure to Delhi</li>
                      <li>End of tour with beautiful memories</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Transportation from Delhi to Delhi</li>
                    <li><FaCheck className="check-icon" />5 Nights accommodation in hotels/houseboats</li>
                    <li><FaCheck className="check-icon" />Daily breakfast and dinner</li>
                    <li><FaCheck className="check-icon" />All transfers and sightseeing</li>
                    <li><FaCheck className="check-icon" />Shikara ride on Dal Lake</li>
                    <li><FaCheck className="check-icon" />All toll taxes and parking charges</li>
                    <li><FaCheck className="check-icon" />Tour guide throughout the trip</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Flight tickets</li>
                    <li><FaTimes className="times-icon" />Gondola ride charges</li>
                    <li><FaTimes className="times-icon" />Horse riding charges</li>
                    <li><FaTimes className="times-icon" />Thajiwas Glacier charges</li>
                    <li><FaTimes className="times-icon" />Any personal expenses</li>
                    <li><FaTimes className="times-icon" />Anything not mentioned in inclusions</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 17,999</span>
                    <span className="duration">6 Days / 5 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Kashmir, Jammu & Kashmir</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: March to November</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Easy</span>
                    </div>
                  </div>

                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Kashmir Paradise Tour"
                  tourPrice="₹ 17,999"
                  tourDuration="6 Days / 5 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Kashmir;
